import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Mic, MicOff, Loader2 } from "lucide-react";

/** Browser-feature detection — Web Speech API is the spec name but only Chromium
 * exposes it as `webkitSpeechRecognition`. We hide the mic button entirely in
 * unsupported browsers (rather than show a button that does nothing).
 */
export const isSpeechRecognitionSupported = () =>
  typeof window !== "undefined" &&
  (("SpeechRecognition" in window) || ("webkitSpeechRecognition" in window));

const getRecognitionClass = () =>
  (typeof window !== "undefined" &&
    (window.SpeechRecognition || window.webkitSpeechRecognition)) || null;

// How long the user can pause before recording auto-ends (ms).
const SILENCE_END_MS = 3000;
// How long after the last spoken word before we fire an *incremental* parse
// so the form fills in real-time while the user is still thinking (ms).
const INCREMENTAL_PARSE_MS = 1200;

/**
 * VoiceMicButton — captures speech with `continuous: true` so natural pauses
 * don't kill the session. We manage silence ourselves:
 *   - 1.2 s of silence  → fire `onIncrementalTranscript(text)` so the parent
 *     can update form fields in real time without stopping the mic.
 *   - 3.0 s of silence  → end recording, fire `onTranscript(text)` with the
 *     final accumulated transcript.
 * The two callbacks are de-duped at the parent level (parent tracks the last
 * text it parsed and skips re-parsing identical input).
 *
 * Props:
 *   onTranscript(text)             — fired once when the mic finally stops
 *   onIncrementalTranscript(text)  — optional, fired on every 1.2 s pause
 *   busy                           — parent's spinner state (e.g. LLM in-flight)
 *   label, size, testid, hint, disabled
 */
export const VoiceMicButton = ({
  onTranscript,
  onIncrementalTranscript,
  busy = false,
  label,
  size = "lg",
  testid,
  hint,
  disabled = false,
}) => {
  const supported = isSpeechRecognitionSupported();
  const [listening, setListening] = useState(false);
  const [interim, setInterim] = useState("");
  const [error, setError] = useState(null);
  // Drives the silence countdown ring: a key that resets each time we hear
  // speech (so framer-motion replays the 3-second sweep from full).
  const [silenceKey, setSilenceKey] = useState(0);
  const recognitionRef = useRef(null);

  // Mutable refs so the timer callbacks always see the latest values
  // without re-creating the recognition instance.
  const finalTextRef = useRef("");
  const lastEmittedRef = useRef("");
  const endTimerRef = useRef(null);
  const incrementalTimerRef = useRef(null);

  const clearTimers = () => {
    if (endTimerRef.current) clearTimeout(endTimerRef.current);
    if (incrementalTimerRef.current) clearTimeout(incrementalTimerRef.current);
    endTimerRef.current = null;
    incrementalTimerRef.current = null;
  };

  useEffect(() => () => {
    clearTimers();
    try { recognitionRef.current?.stop(); } catch { /* noop */ }
  }, []);

  if (!supported) return null;

  const emitIncremental = () => {
    const text = (finalTextRef.current + " " + interim).trim().replace(/\s+/g, " ");
    if (!text || text === lastEmittedRef.current) return;
    lastEmittedRef.current = text;
    onIncrementalTranscript?.(text);
  };

  const start = () => {
    if (busy || disabled) return;
    setError(null);
    setInterim("");
    finalTextRef.current = "";
    lastEmittedRef.current = "";
    clearTimers();

    const Recognition = getRecognitionClass();
    const rec = new Recognition();
    rec.continuous = true;            // keep listening through natural pauses
    rec.interimResults = true;
    rec.lang = "en-US";
    rec.maxAlternatives = 1;

    rec.onresult = (event) => {
      let interimChunk = "";
      for (let i = event.resultIndex; i < event.results.length; i += 1) {
        const transcript = event.results[i][0].transcript;
        if (event.results[i].isFinal) {
          finalTextRef.current = (finalTextRef.current + " " + transcript).trim();
        } else {
          interimChunk += transcript;
        }
      }
      setInterim(interimChunk);

      // Every new speech event resets both pause timers.
      clearTimers();
      // Replay the silence countdown ring from full.
      setSilenceKey((k) => k + 1);
      // Real-time incremental: fire after 1.2 s of silence so fields update
      // while the user is still composing their next thought.
      incrementalTimerRef.current = setTimeout(emitIncremental, INCREMENTAL_PARSE_MS);
      // Hard end: 3 s of silence and we close the session for them.
      endTimerRef.current = setTimeout(() => {
        try { rec.stop(); } catch (e) { void e; }
      }, SILENCE_END_MS);
    };

    rec.onerror = (event) => {
      const code = event?.error || "unknown";
      const map = {
        "not-allowed": "Microphone blocked — allow mic access for this site.",
        "no-speech":   "I didn't catch that — try again.",
        "audio-capture": "No microphone detected.",
        "network":     "Network glitch on speech-to-text — try again.",
        "aborted":     null, // user-initiated stop, silently ignore
      };
      const msg = map[code];
      if (msg) setError(msg);
      else if (msg === undefined) setError(`Microphone error (${code})`);
      setListening(false);
    };

    rec.onend = () => {
      clearTimers();
      setListening(false);
      const finalText = (finalTextRef.current + " " + interim)
        .trim()
        .replace(/\s+/g, " ");
      setInterim("");
      if (finalText) {
        // Mark this transcript as emitted so the parent's dedup guard works
        // even if onTranscript and the last onIncremental carry the same text.
        lastEmittedRef.current = finalText;
        onTranscript?.(finalText);
      }
    };

    try {
      rec.start();
      recognitionRef.current = rec;
      setListening(true);
      // Also arm the silence end-timer the moment we start, in case the user
      // never speaks at all.
      endTimerRef.current = setTimeout(() => {
        try { rec.stop(); } catch (e) { void e; }
      }, SILENCE_END_MS);
    } catch (e) {
      setError("Couldn't start microphone — try again.");
      setListening(false);
    }
  };

  const stop = () => {
    clearTimers();
    try { recognitionRef.current?.stop(); } catch { /* noop */ }
  };

  const big = size === "lg";

  return (
    <div className="flex flex-col items-start" data-testid={`${testid}-wrap`}>
      <button
        type="button"
        onClick={listening ? stop : start}
        disabled={busy || disabled}
        data-testid={testid}
        aria-pressed={listening}
        aria-label={listening ? "Stop voice input" : "Start voice input"}
        className={`relative inline-flex items-center gap-2 rounded-full transition-all ${
          big ? "px-4 py-2.5 text-sm" : "px-3 py-1.5 text-xs"
        } ${
          listening
            ? "bg-[#D95D39] text-white shadow-[0_0_0_4px_rgba(217,93,57,0.18)]"
            : busy
            ? "bg-[#1F5566] text-white"
            : "bg-white border-2 border-[#277A82] text-[#1F5566] hover:bg-[#DCEDF0]"
        } ${(busy || disabled) ? "opacity-70 cursor-not-allowed" : ""}`}
      >
        <span className="relative flex items-center justify-center">
          {busy ? (
            <Loader2 className={big ? "w-4 h-4 animate-spin" : "w-3.5 h-3.5 animate-spin"} />
          ) : listening ? (
            <MicOff className={big ? "w-4 h-4" : "w-3.5 h-3.5"} />
          ) : (
            <Mic className={big ? "w-4 h-4" : "w-3.5 h-3.5"} />
          )}
          {listening && (
            <>
              {/* Soft pulsing halo */}
              <motion.span
                className="absolute inset-0 rounded-full border-2 border-[#D95D39]"
                animate={{ scale: [1, 1.8], opacity: [0.6, 0] }}
                transition={{ duration: 1.2, repeat: Infinity, ease: "easeOut" }}
              />
              {/* 3-second silence countdown ring — replays each time we hear speech */}
              <svg
                key={silenceKey}
                viewBox="0 0 36 36"
                className={`absolute ${big ? "w-7 h-7 -inset-1.5" : "w-6 h-6 -inset-1"} pointer-events-none`}
                data-testid={`${testid}-countdown-ring`}
                aria-hidden="true"
              >
                <circle cx="18" cy="18" r="16" fill="none" stroke="rgba(255,255,255,0.25)" strokeWidth="2.5" />
                <motion.circle
                  cx="18" cy="18" r="16" fill="none"
                  stroke="#FFFFFF" strokeWidth="2.5" strokeLinecap="round"
                  strokeDasharray={`${2 * Math.PI * 16}`}
                  initial={{ strokeDashoffset: 0, rotate: -90 }}
                  animate={{ strokeDashoffset: 2 * Math.PI * 16 }}
                  transition={{ duration: 3, ease: "linear" }}
                  style={{ transformOrigin: "18px 18px", transform: "rotate(-90deg)" }}
                />
              </svg>
            </>
          )}
        </span>
        <span className="font-semibold whitespace-nowrap">
          {busy ? "Thinking…" : listening ? "Listening — tap to stop" : (label || "Speak")}
        </span>
      </button>
      <AnimatePresence>
        {(interim || error || hint) && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="mt-2 max-w-full"
          >
            {error ? (
              <div
                className="text-[11px] text-[#9C3E1D] font-semibold"
                data-testid={`${testid}-error`}
              >
                {error}
              </div>
            ) : interim ? (
              <div
                className="text-[11px] text-[#1F5566] italic max-w-[280px]"
                data-testid={`${testid}-interim`}
              >
                “{interim}”
              </div>
            ) : (
              <div className="text-[11px] text-[#8A93A6]">{hint}</div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
